import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import Avatar from '../components/Avatar';
import { getImageUrl } from '../utils/imageUrl';
import {
  getPlanters,
  Planter,
  createPlanter,
  uploadPlanterHeadshot,
  uploadPlanterFamilyPhoto,
  formatPlanterLocation,
  sendPlanterAppInvite,
} from '../api/plantersService';
import { usersService, HubUser } from '../api/usersService';
import { useAuth } from '../contexts/AuthContext';

const Planters: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const hasRole = (roleName: string) => {
    return !!user?.roles?.some((r: any) => (typeof r === 'string' ? r : r.name) === roleName);
  };
  const canCreate = hasRole('admin') || hasRole('staff') || hasRole('hub_leader');
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [pathKindFilter, setPathKindFilter] = useState(searchParams.get('pathKind') || '');
  const [page, setPage] = useState(Number(searchParams.get('page')) || 1);

  useEffect(() => {
    setSearch(searchParams.get('search') || '');
    setPathKindFilter(searchParams.get('pathKind') || '');
    setPage(Number(searchParams.get('page')) || 1);
  }, [searchParams]);

  const effectivePlantingStepParam = searchParams.get('effectivePlantingStep') || '';
  const plantingStepParam = searchParams.get('plantingStep') || '';
  const pathKindUnsetParam = searchParams.get('pathKindUnset') || '';
  const journeyEndedParam = searchParams.get('journeyEnded') || '';
  const journeyEndReasonParam = searchParams.get('journeyEndReason') || '';

  const { data, isLoading, error } = useQuery({
    queryKey: [
      'planters',
      {
        search,
        pathKind: pathKindFilter,
        plantingStep: plantingStepParam,
        effectivePlantingStep: effectivePlantingStepParam,
        pathKindUnset: pathKindUnsetParam,
        journeyEnded: journeyEndedParam,
        journeyEndReason: journeyEndReasonParam,
        page,
        limit: 20,
      },
    ],
    queryFn: () =>
      getPlanters({
        search,
        pathKind: pathKindFilter || undefined,
        ...(plantingStepParam ? { plantingStep: plantingStepParam } : {}),
        ...(effectivePlantingStepParam === 'funding' || effectivePlantingStepParam === 'post_funding'
          ? { effectivePlantingStep: effectivePlantingStepParam as 'funding' | 'post_funding' }
          : {}),
        ...(pathKindUnsetParam === 'true' ? { pathKindUnset: 'true' } : {}),
        ...(journeyEndedParam === 'true' || journeyEndedParam === 'false'
          ? { journeyEnded: journeyEndedParam as 'true' | 'false' }
          : {}),
        ...(journeyEndReasonParam ? { journeyEndReason: journeyEndReasonParam } : {}),
        page,
        limit: 20,
      }),
  });

  const planters = data?.data || [];
  const pagination = data?.pagination;

  // Coaches list (only when needed)
  const [showAddModal, setShowAddModal] = useState(false);
  const [headshotFile, setHeadshotFile] = useState<File | null>(null);
  const [familyPhotoFile, setFamilyPhotoFile] = useState<File | null>(null);
  const { data: coachesData } = useQuery({
    queryKey: ['coaches'],
    queryFn: () => usersService.getUsersByRole('coach'),
    enabled: showAddModal,
  });
  const coaches: HubUser[] = coachesData?.data || [];


  const [addForm, setAddForm] = useState({
    name: '',
    spouseName: '',
    email: '',
    phone: '',
    secondaryPhone: '',
    city: '',
    state: '',
    churchName: '',
    assignedCoachId: '' as string,
    pathKind: '' as '' | 'growth' | 'planting',
    plantingStep: 'training' as 'training' | 'funding' | 'post_funding',
    fundingEndDate: '',
  });

  // Mutations
  const createPlanterMutation = useMutation({
    mutationFn: async () => {
      if (!addForm.name) throw new Error('Name is required');
      const payload: any = {
        name: addForm.name,
        spouseName: addForm.spouseName || undefined,
        email: addForm.email.trim() || undefined,
        phone: addForm.phone.trim() || undefined,
        secondaryPhone: addForm.secondaryPhone.trim() || undefined,
        city: addForm.city.trim() || undefined,
        state: addForm.state.trim() || undefined,
        churchName: addForm.churchName || undefined,
      };
      if (addForm.assignedCoachId) payload.assignedCoach = parseInt(addForm.assignedCoachId);
      if (addForm.pathKind === 'growth') {
        payload.pathKind = 'growth';
      } else if (addForm.pathKind === 'planting') {
        payload.pathKind = 'planting';
        payload.plantingStep = addForm.plantingStep;
        if (addForm.plantingStep !== 'training' && addForm.fundingEndDate) {
          payload.fundingEndDate = addForm.fundingEndDate;
        }
      }
      const newPlanter = await createPlanter(payload);
      
      // Upload photos if provided
      if (headshotFile && newPlanter.id) {
        await uploadPlanterHeadshot(newPlanter.id, headshotFile);
      }
      if (familyPhotoFile && newPlanter.id) {
        await uploadPlanterFamilyPhoto(newPlanter.id, familyPhotoFile);
      }
      
      return newPlanter;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['planters'] });
      setShowAddModal(false);
      setAddForm({
        name: '',
        spouseName: '',
        email: '',
        phone: '',
        secondaryPhone: '',
        city: '',
        state: '',
        churchName: '',
        assignedCoachId: '',
        pathKind: '',
        plantingStep: 'training',
        fundingEndDate: '',
      });
      setHeadshotFile(null);
      setFamilyPhotoFile(null);
    },
  });

  // App invite handling (per-row)
  const [invitedIds, setInvitedIds] = useState<Set<number>>(new Set());
  const inviteAppMutation = useMutation({
    mutationFn: (planterId: number) => sendPlanterAppInvite(planterId),
    onSuccess: (_data, planterId) => {
      setInvitedIds((prev) => new Set(prev).add(planterId));
    },
  });

  const hasLinkedAccount = (p: Planter) =>
    p.hasPlanterAppUserLinked === true ||
    (p.hasPlanterAppUserLinked === undefined && Boolean(p.user?.id || p.userId));

  const canSendAppInvite = (p: Planter) =>
    hasRole('admin') ||
    hasRole('staff') ||
    (hasRole('hub_leader') && p.assignedHubLeader === user?.id);

  const handleSearch = (value: string) => {
    setSearch(value);
    setPage(1);
    updateSearchParams({ search: value, page: '1' });
  };

  const handlePathKindFilter = (value: string) => {
    setPathKindFilter(value);
    setPage(1);
    const newParams = new URLSearchParams(searchParams);
    if (value) newParams.set('pathKind', value);
    else newParams.delete('pathKind');
    newParams.delete('effectivePlantingStep');
    newParams.delete('plantingStep');
    newParams.delete('pathKindUnset');
    newParams.set('page', '1');
    setSearchParams(newParams);
  };

  const updateSearchParams = (updates: Record<string, string>) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value) {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });
    setSearchParams(newParams);
  };

  const plantingStepLabel = (p: Planter) => {
    const step = p.effectivePlantingStep ?? p.plantingStep;
    if (!step) return '';
    const labels: Record<string, string> = {
      training: 'Training',
      funding: 'Funding',
      post_funding: 'Post-funding',
    };
    return labels[step] || step;
  };

  return (
    <Layout>
      <div className="px-4 sm:px-0">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Church Planters Directory</h1>
          <p className="mt-2 text-sm sm:text-base text-gray-600">
            Manage and track all church planters in your network
          </p>
        </div>

        {(effectivePlantingStepParam ||
          plantingStepParam ||
          pathKindUnsetParam ||
          journeyEndedParam ||
          journeyEndReasonParam) && (
          <div className="mb-4 rounded-lg border border-primary-100 bg-primary-50 px-4 py-3 text-sm text-primary-900">
            Showing a pipeline slice from the overview.{' '}
            <Link to="/planters/pipeline" className="font-medium underline hover:no-underline">
              Back to pipeline
            </Link>
          </div>
        )}

        {/* Header actions and Filters */}
        <div className="bg-white shadow-sm rounded-xl mb-6">
          <div className="p-4 sm:p-6">
            <div className="space-y-4">
              {/* Actions */}
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => navigate('/planters/pipeline')}
                    className="inline-flex items-center px-3 py-2 border border-primary-200 text-sm leading-4 font-medium rounded-md text-primary-800 bg-primary-50 hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    Pipeline overview
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/map')}
                    className="inline-flex items-center px-3 py-2 border border-gray-200 text-sm leading-4 font-medium rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A2 2 0 013 15.382V5.618a2 2 0 011.553-1.894L9 1m0 19l6-3m-6 3V1m6 16l5.447 2.724A2 2 0 0021 17.382V7.618a2 2 0 00-1.553-1.894L15 3m0 14V3m0 0L9 1" />
                    </svg>
                    Map View
                  </button>
                </div>
                {canCreate && (
                  <button
                    onClick={() => {
                      setShowAddModal(true);
                    }}
                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add Planter
                  </button>
                )}
              </div>
              {/* Search */}
              <div>
                <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-2">
                  Search
                </label>
                <input
                  type="text"
                  id="search"
                  placeholder="Search by name, city, state..."
                  value={search}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-base px-4 py-3 min-h-touch"
                />
              </div>

              <div>
                <label htmlFor="pathKind" className="block text-sm font-medium text-gray-700 mb-2">
                  Path
                </label>
                <select
                  id="pathKind"
                  value={pathKindFilter}
                  onChange={(e) => handlePathKindFilter(e.target.value)}
                  className="block w-full max-w-md rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-base px-4 py-3 min-h-touch"
                >
                  <option value="">All paths</option>
                  <option value="growth">Growth</option>
                  <option value="planting">Planting</option>
                </select>
              </div>
            </div>

            {/* Clear Filters */}
            {(search ||
              pathKindFilter ||
              effectivePlantingStepParam ||
              plantingStepParam ||
              pathKindUnsetParam ||
              journeyEndedParam ||
              journeyEndReasonParam) && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setSearch('');
                    setPathKindFilter('');
                    setSearchParams({});
                  }}
                  className="text-sm font-medium text-primary-600 hover:text-primary-700 py-2 min-h-touch inline-flex items-center"
                >
                  <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Clear all filters
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Planters List */}
        {isLoading ? (
          <div className="bg-white shadow rounded-lg p-8">
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              <span className="ml-3 text-gray-600">Loading planters...</span>
            </div>
          </div>
        ) : error ? (
          <div className="bg-white shadow rounded-lg p-8">
            <div className="text-center text-red-600">
              Error loading planters. Please try again.
            </div>
          </div>
        ) : planters.length === 0 ? (
          <div className="bg-white shadow rounded-lg p-8">
            <div className="text-center text-gray-500">
              No planters found. Try adjusting your filters.
            </div>
          </div>
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="hidden lg:block bg-white shadow rounded-lg overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Location
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      App account
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Hub
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Pipeline
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Coach
                    </th>
                    <th scope="col" className="relative px-6 py-3">
                      <span className="sr-only">View</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {planters.map((planter: Planter) => (
                    <tr 
                      key={planter.id} 
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => navigate(`/planters/${planter.id}`)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Avatar name={planter.name} size="md" className="mr-3" imageUrl={getImageUrl(planter.headshotImagePath, { width: 96, quality: 70, format: 'webp' })} />
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {planter.name}
                            </div>
                            {planter.spouseName && (
                              <div className="text-sm text-gray-500">
                                & {planter.spouseName}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {planter.email ? (
                          <a
                            href={`mailto:${planter.email}`}
                            onClick={(e) => e.stopPropagation()}
                            className="block text-sm text-primary-600 hover:text-primary-900 break-all"
                          >
                            {planter.email}
                          </a>
                        ) : (
                          <div className="text-sm text-gray-400">No email</div>
                        )}
                        {planter.phone ? (
                          <a
                            href={`tel:${planter.phone}`}
                            onClick={(e) => e.stopPropagation()}
                            className="block text-sm text-gray-600 hover:text-gray-900 mt-0.5"
                          >
                            {planter.phone}
                          </a>
                        ) : (
                          <div className="text-sm text-gray-400 mt-0.5">No phone</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatPlanterLocation(planter) || '-'}
                        </div>
                        {planter.churchName && (
                          <div className="text-sm text-gray-500">
                            {planter.churchName}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {hasLinkedAccount(planter) ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            App account linked
                          </span>
                        ) : (
                          <div className="space-y-1">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                              No app account
                            </span>
                            {canSendAppInvite(planter) && (
                              invitedIds.has(planter.id) ? (
                                <div className="text-xs text-green-700">Invitation sent</div>
                              ) : (
                                <button
                                  type="button"
                                  disabled={
                                    !planter.email?.trim() ||
                                    (inviteAppMutation.isPending &&
                                      inviteAppMutation.variables === planter.id)
                                  }
                                  title={
                                    !planter.email?.trim()
                                      ? 'Add an email on this profile to send an invite'
                                      : undefined
                                  }
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    inviteAppMutation.mutate(planter.id);
                                  }}
                                  className="block text-xs font-medium text-primary-600 hover:text-primary-900 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {inviteAppMutation.isPending &&
                                  inviteAppMutation.variables === planter.id
                                    ? 'Sending…'
                                    : 'Invite to app'}
                                </button>
                              )
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {planter.assignedHub || <span className="text-gray-400">Unassigned</span>}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          {planter.hubLeader
                            ? `${planter.hubLeader.firstName} ${planter.hubLeader.lastName}`
                            : 'No hub leader'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {planter.pathKind ? (
                          <div className="text-sm text-gray-900">
                            <span className="font-medium">
                              {planter.pathKind === 'growth' ? 'Growth path' : 'Planting path'}
                            </span>
                            {planter.pathKind === 'planting' && plantingStepLabel(planter) && (
                              <div className="text-xs text-gray-500 mt-1">
                                {plantingStepLabel(planter)}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {planter.coach ? (
                          `${planter.coach.firstName} ${planter.coach.lastName}`
                        ) : (
                          <span className="text-gray-400">Unassigned</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-3">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/planters/${planter.id}`);
                          }}
                          className="text-primary-600 hover:text-primary-900"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="lg:hidden space-y-4">
              {planters.map((planter: Planter) => (
                <div
                  key={planter.id}
                  className="bg-white shadow rounded-xl overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => navigate(`/planters/${planter.id}`)}
                >
                <div className="p-4 space-y-3">
                    {/* Header with Avatar and Name */}
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3 flex-1 min-w-0">
                        <Avatar name={planter.name} size="lg" imageUrl={getImageUrl(planter.headshotImagePath, { width: 128, quality: 70, format: 'webp' })} />
                        <div className="flex-1 min-w-0">
                          <h3 className="text-base font-semibold text-gray-900 truncate">
                            {planter.name}
                          </h3>
                          {planter.spouseName && (
                            <p className="text-sm text-gray-500 truncate">
                              & {planter.spouseName}
                            </p>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/planters/${planter.id}`);
                        }}
                        className="flex-shrink-0 ml-2 p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors min-w-touch min-h-touch flex items-center justify-center"
                      >
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>

                    {/* Location and Church */}
                    {(formatPlanterLocation(planter) || planter.churchName) && (
                      <div className="flex items-start text-sm text-gray-600">
                        <svg className="h-5 w-5 mr-2 flex-shrink-0 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <div className="flex-1">
                          <div>{formatPlanterLocation(planter) || 'City / state not specified'}</div>
                          {planter.churchName && (
                            <div className="text-gray-500">{planter.churchName}</div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Contact */}
                    {(planter.email || planter.phone) && (
                      <div className="flex items-start text-sm text-gray-600">
                        <svg className="h-5 w-5 mr-2 flex-shrink-0 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        <div className="flex-1 min-w-0">
                          {planter.email && (
                            <a
                              href={`mailto:${planter.email}`}
                              onClick={(e) => e.stopPropagation()}
                              className="block text-primary-600 break-all"
                            >
                              {planter.email}
                            </a>
                          )}
                          {planter.phone && (
                            <a
                              href={`tel:${planter.phone}`}
                              onClick={(e) => e.stopPropagation()}
                              className="block text-gray-600"
                            >
                              {planter.phone}
                            </a>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Hub */}
                    <div className="flex items-start text-sm text-gray-600">
                      <svg className="h-5 w-5 mr-2 flex-shrink-0 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                      <div className="flex-1 min-w-0">
                        <div>{planter.assignedHub || 'Unassigned hub'}</div>
                        <div className="text-gray-500">
                          {planter.hubLeader
                            ? `${planter.hubLeader.firstName} ${planter.hubLeader.lastName}`
                            : 'No hub leader'}
                        </div>
                      </div>
                    </div>

                    {/* App account */}
                    <div className="flex flex-wrap items-center gap-2">
                      {hasLinkedAccount(planter) ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          App account linked
                        </span>
                      ) : (
                        <>
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                            No app account
                          </span>
                          {canSendAppInvite(planter) && (
                            invitedIds.has(planter.id) ? (
                              <span className="text-xs text-green-700">Invitation sent</span>
                            ) : (
                              <button
                                type="button"
                                disabled={
                                  !planter.email?.trim() ||
                                  (inviteAppMutation.isPending &&
                                    inviteAppMutation.variables === planter.id)
                                }
                                onClick={(e) => {
                                  e.stopPropagation();
                                  inviteAppMutation.mutate(planter.id);
                                }}
                                className="text-xs font-medium text-primary-600 hover:text-primary-900 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {inviteAppMutation.isPending &&
                                inviteAppMutation.variables === planter.id
                                  ? 'Sending…'
                                  : 'Invite to app'}
                              </button>
                            )
                          )}
                        </>
                      )}
                    </div>

                    {planter.pathKind && (
                      <div className="flex flex-wrap gap-2">
                        <span className="inline-flex px-3 py-1 text-xs font-semibold rounded-full bg-slate-100 text-slate-800">
                          {planter.pathKind === 'growth' ? 'Growth path' : 'Planting path'}
                          {planter.pathKind === 'planting' && plantingStepLabel(planter)
                            ? ` · ${plantingStepLabel(planter)}`
                            : ''}
                        </span>
                      </div>
                    )}

                    {/* Coach */}
                    {planter.coach && (
                      <div className="flex items-center text-sm text-gray-600 pt-2 border-t border-gray-100">
                        <svg className="h-4 w-4 mr-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        <span className="font-medium">Coach:</span>
                        <span className="ml-1">{planter.coach.firstName} {planter.coach.lastName}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {pagination && pagination.pages > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 mt-4 rounded-lg shadow">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage(Math.min(pagination.pages, page + 1))}
                    disabled={page === pagination.pages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Showing <span className="font-medium">{((page - 1) * (pagination.limit || 20)) + 1}</span> to{' '}
                      <span className="font-medium">{Math.min(page * (pagination.limit || 20), pagination.total)}</span> of{' '}
                      <span className="font-medium">{pagination.total}</span> results
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        onClick={() => setPage(Math.max(1, page - 1))}
                        disabled={page === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span className="sr-only">Previous</span>
                        <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                          <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </button>
                      <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                        Page {page} of {pagination.pages}
                      </span>
                      <button
                        onClick={() => setPage(Math.min(pagination.pages, page + 1))}
                        disabled={page === pagination.pages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span className="sr-only">Next</span>
                        <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                          <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}

            {/* Add Planter Modal */}
            {showAddModal && canCreate && (
              <div className="fixed z-[60] inset-0 overflow-y-auto">
                <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                  <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowAddModal(false)}></div>
                  <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
                  <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full max-h-[90vh] overflow-y-auto">
                    <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                      <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Add Church Planter</h3>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Full Name *</label>
                          <input
                            type="text"
                            value={addForm.name}
                            onChange={(e) => setAddForm({ ...addForm, name: e.target.value })}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Spouse Name</label>
                          <input
                            type="text"
                            value={addForm.spouseName}
                            onChange={(e) => setAddForm({ ...addForm, spouseName: e.target.value })}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                          />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Email</label>
                            <input
                              type="email"
                              value={addForm.email}
                              onChange={(e) => setAddForm({ ...addForm, email: e.target.value })}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Phone</label>
                            <input
                              type="text"
                              value={addForm.phone}
                              onChange={(e) => setAddForm({ ...addForm, phone: e.target.value })}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Secondary phone</label>
                          <input
                            type="text"
                            value={addForm.secondaryPhone}
                            onChange={(e) => setAddForm({ ...addForm, secondaryPhone: e.target.value })}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                          />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">City</label>
                            <input
                              type="text"
                              value={addForm.city}
                              onChange={(e) => setAddForm({ ...addForm, city: e.target.value })}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">State</label>
                            <input
                              type="text"
                              value={addForm.state}
                              onChange={(e) => setAddForm({ ...addForm, state: e.target.value })}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Church Name</label>
                          <input
                            type="text"
                            value={addForm.churchName}
                            onChange={(e) => setAddForm({ ...addForm, churchName: e.target.value })}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Assign Coach (optional)</label>
                          <select
                            value={addForm.assignedCoachId}
                            onChange={(e) => setAddForm({ ...addForm, assignedCoachId: e.target.value })}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                          >
                            <option value="">Unassigned</option>
                            {coaches.map(c => (
                              <option key={c.id} value={c.id}>{c.firstName} {c.lastName}</option>
                            ))}
                          </select>
                        </div>
                        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-3">
                          <p className="text-sm font-medium text-gray-900">Pipeline (optional)</p>
                          <p className="text-xs text-gray-600">
                            Choose a path now, or leave unset to place them in “needs path” until you assign one.
                          </p>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Path</label>
                            <select
                              value={addForm.pathKind}
                              onChange={(e) =>
                                setAddForm({
                                  ...addForm,
                                  pathKind: e.target.value as '' | 'growth' | 'planting',
                                })
                              }
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                            >
                              <option value="">Unset (needs path)</option>
                              <option value="growth">Growth path</option>
                              <option value="planting">Planting path</option>
                            </select>
                          </div>
                          {addForm.pathKind === 'planting' && (
                            <>
                              <div>
                                <label className="block text-sm font-medium text-gray-700">Planting step</label>
                                <select
                                  value={addForm.plantingStep}
                                  onChange={(e) =>
                                    setAddForm({
                                      ...addForm,
                                      plantingStep: e.target.value as 'training' | 'funding' | 'post_funding',
                                    })
                                  }
                                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                >
                                  <option value="training">Training</option>
                                  <option value="funding">Funding</option>
                                  <option value="post_funding">Post-funding</option>
                                </select>
                              </div>
                              {addForm.plantingStep !== 'training' && (
                                <div>
                                  <label className="block text-sm font-medium text-gray-700">
                                    Funding end date (optional)
                                  </label>
                                  <input
                                    type="date"
                                    value={addForm.fundingEndDate}
                                    onChange={(e) => setAddForm({ ...addForm, fundingEndDate: e.target.value })}
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                  />
                                </div>
                              )}
                            </>
                          )}
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Headshot (optional)</label>
                            {headshotFile ? (
                              <div className="space-y-2">
                                <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded px-3 py-2">
                                  <span className="text-sm text-gray-700 truncate">{headshotFile.name}</span>
                                  <button
                                    type="button"
                                    onClick={() => setHeadshotFile(null)}
                                    className="text-sm text-red-600 hover:text-red-700 ml-2"
                                  >
                                    Remove
                                  </button>
                                </div>
                                <img
                                  src={URL.createObjectURL(headshotFile)}
                                  alt="Headshot preview"
                                  className="w-full h-32 object-cover rounded border"
                                />
                              </div>
                            ) : (
                              <div>
                                <input
                                  id="add-headshot-upload"
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) setHeadshotFile(file);
                                  }}
                                />
                                <label
                                  htmlFor="add-headshot-upload"
                                  className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 cursor-pointer w-full justify-center"
                                >
                                  Choose File
                                </label>
                              </div>
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Family Photo (optional)</label>
                            {familyPhotoFile ? (
                              <div className="space-y-2">
                                <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded px-3 py-2">
                                  <span className="text-sm text-gray-700 truncate">{familyPhotoFile.name}</span>
                                  <button
                                    type="button"
                                    onClick={() => setFamilyPhotoFile(null)}
                                    className="text-sm text-red-600 hover:text-red-700 ml-2"
                                  >
                                    Remove
                                  </button>
                                </div>
                                <img
                                  src={URL.createObjectURL(familyPhotoFile)}
                                  alt="Family preview"
                                  className="w-full h-32 object-cover rounded border"
                                />
                              </div>
                            ) : (
                              <div>
                                <input
                                  id="add-family-upload"
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) setFamilyPhotoFile(file);
                                  }}
                                />
                                <label
                                  htmlFor="add-family-upload"
                                  className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 cursor-pointer w-full justify-center"
                                >
                                  Choose File
                                </label>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                      <button
                        type="button"
                        disabled={createPlanterMutation.isPending || !addForm.name}
                        onClick={() => createPlanterMutation.mutate()}
                        className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                      >
                        {createPlanterMutation.isPending ? 'Creating...' : 'Create'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowAddModal(false)}
                        className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

          </>
        )}
      </div>
    </Layout>
  );
};

export default Planters;

